import type AccordionPanel from './AccordionPanel.js'
import type AccordionTrigger from './AccordionTrigger.js'

import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { observe } from '../common/decorators/observe.js'
import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import style from './AccordionItem.css'

/**
 * Accordion Item groups an [Accordion Header](/components/accordion-header/) and
 * its [Accordion Panel](/components/accordion-panel/) within an
 * [Accordion](/components/accordion/).
 *
 * @status draft
 * @category container
 * @lightDom true
 *
 * @slot - Default slot for an [Accordion Header](/components/accordion-header/) and an [Accordion Panel](/components/accordion-panel/).
 */
@customElement('nord-accordion-item')
export default class AccordionItem extends LitElement {
  /**
   * A unique value identifying this item within its
   * [Accordion](/components/accordion/). Used to track which items are open.
   */
  @property({ reflect: true }) value = ''

  /**
   * Whether this item is open. Coordinated by the parent
   * [Accordion](/components/accordion/).
   */
  @property({ reflect: true, type: Boolean }) open = false

  /**
   * Whether this item is disabled.
   */
  @property({ reflect: true, type: Boolean }) disabled = false

  /**
   * Whether the parent [Accordion](/components/accordion/) is disabled. Combined
   * with `disabled` to determine the effective disabled state. Reactive (and
   * observed below) so toggling the parent's `disabled` after setup re-syncs the
   * trigger's disabled/aria-disabled/tabindex.
   * @internal
   */
  @property({ attribute: false }) parentDisabled = false

  /**
   * Stable id base assigned by the parent [Accordion](/components/accordion/).
   * Persists across re-setup so ids are never reassigned to an item that
   * already has them.
   * @internal
   */
  partsId?: string

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
  }

  // Render in the light DOM so the header and panel stay the element's own
  // content and the injected stylesheet applies (no shadow boundary, like Table).
  protected createRenderRoot() {
    return this
  }

  private get trigger() {
    return this.querySelector<AccordionTrigger>('nord-accordion-trigger')
  }

  private get panel() {
    return this.querySelector<AccordionPanel>('nord-accordion-panel')
  }

  private get isDisabled() {
    return this.disabled || this.parentDisabled
  }

  /**
   * Wire up the ARIA relationship between the trigger and the panel. Called by
   * the parent [Accordion](/components/accordion/).
   * @internal
   */
  setupParts(id: string) {
    const { trigger, panel } = this

    if (trigger) {
      trigger.id ||= `${id}-trigger`
      trigger.open = this.open
      trigger.disabled = this.isDisabled
    }

    if (panel) {
      panel.id ||= `${id}-panel`
      panel.open = this.open
      if (trigger) {
        trigger.setAttribute('aria-controls', panel.id)
        panel.setAttribute('aria-labelledby', trigger.id)
      }
    }
  }

  @observe('open')
  protected handleOpenChange() {
    this.propagate()
  }

  /**
   * Synchronously push the open state (and ARIA) to the trigger and panel.
   * Called by the parent [Accordion](/components/accordion/) and on local changes.
   * @internal
   */
  propagate() {
    const { trigger, panel } = this
    if (trigger) {
      trigger.open = this.open
      trigger.setAttribute('aria-expanded', `${this.open}`)
    }
    if (panel) {
      panel.open = this.open
    }
  }

  @observe('disabled')
  @observe('parentDisabled')
  protected handleDisabledChange() {
    this.updateTriggerDisabled()
  }

  private updateTriggerDisabled() {
    const { trigger } = this
    if (trigger) {
      trigger.disabled = this.isDisabled
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-accordion-item': AccordionItem
  }
}
